export type ShapeName = "1x1" | "2x1" | "1x2" | "3x1" | "1x3"
export type TileSizeTier = "small" | "medium" | "large"

export interface PlacedTile {
  slug: string
  x: number
  y: number
  shape: ShapeName
}

export interface AutoLayoutSkill {
  slug: string
  skill_md_size?: number | null
}

export const GRID_SIZE = 4

export const SHAPE_OFFSETS: Record<
  ShapeName,
  readonly (readonly [number, number])[]
> = {
  "1x1": [[0, 0]],
  "2x1": [
    [0, 0],
    [1, 0],
  ],
  "1x2": [
    [0, 0],
    [0, 1],
  ],
  "3x1": [
    [0, 0],
    [1, 0],
    [2, 0],
  ],
  "1x3": [
    [0, 0],
    [0, 1],
    [0, 2],
  ],
}

const TIER_ROTATIONS: Record<TileSizeTier, readonly ShapeName[]> = {
  small: ["1x1"],
  medium: ["2x1", "1x2"],
  large: ["3x1", "1x3"],
}

export function getSizeTier(skillMdLength: number): TileSizeTier {
  if (skillMdLength < 2048) return "small"
  if (skillMdLength <= 8192) return "medium"
  return "large"
}

export function getDefaultShape(tier: TileSizeTier): ShapeName {
  switch (tier) {
    case "small":
      return "1x1"
    case "medium":
      return "2x1"
    case "large":
      return "3x1"
  }
}

export function getRotations(tier: TileSizeTier): readonly ShapeName[] {
  return TIER_ROTATIONS[tier]
}

export function nextRotation(
  currentShape: ShapeName,
  tier: TileSizeTier
): ShapeName {
  const rotations = getRotations(tier)
  const currentIndex = rotations.indexOf(currentShape)
  if (currentIndex === -1) {
    return rotations[0]
  }
  return rotations[(currentIndex + 1) % rotations.length]
}

export function getOccupiedCells(
  x: number,
  y: number,
  shape: ShapeName
): [number, number][] {
  const offsets = SHAPE_OFFSETS[shape] ?? SHAPE_OFFSETS["1x1"]
  return offsets.map(([dx, dy]) => [x + dx, y + dy])
}

export function isInBounds(x: number, y: number, shape: ShapeName): boolean {
  return getOccupiedCells(x, y, shape).every(
    ([cx, cy]) => cx >= 0 && cx < GRID_SIZE && cy >= 0 && cy < GRID_SIZE
  )
}

export function buildOccupiedSet(
  tiles: readonly PlacedTile[],
  excludeSlug?: string
): Set<string> {
  const occupied = new Set<string>()
  for (const tile of tiles) {
    if (tile.slug === excludeSlug) continue
    for (const [cx, cy] of getOccupiedCells(tile.x, tile.y, tile.shape)) {
      occupied.add(`${cx},${cy}`)
    }
  }
  return occupied
}

export function hasCollision(
  x: number,
  y: number,
  shape: ShapeName,
  existingTiles: readonly PlacedTile[],
  excludeSlug?: string
): boolean {
  const occupied = buildOccupiedSet(existingTiles, excludeSlug)
  return getOccupiedCells(x, y, shape).some(([cx, cy]) =>
    occupied.has(`${cx},${cy}`)
  )
}

export function canPlace(
  x: number,
  y: number,
  shape: ShapeName,
  existingTiles: readonly PlacedTile[],
  excludeSlug?: string
): boolean {
  return (
    isInBounds(x, y, shape) &&
    !hasCollision(x, y, shape, existingTiles, excludeSlug)
  )
}

export function autoPlace(
  tier: TileSizeTier,
  preferredShape: ShapeName,
  existingTiles: readonly PlacedTile[]
): { x: number; y: number; shape: ShapeName } | null {
  const rotations = getRotations(tier)
  // Try preferred shape first, then all rotations
  const shapesToTry: ShapeName[] = [
    preferredShape,
    ...rotations.filter((s) => s !== preferredShape),
  ]

  for (const shape of shapesToTry) {
    for (let y = 0; y < GRID_SIZE; y++) {
      for (let x = 0; x < GRID_SIZE; x++) {
        if (canPlace(x, y, shape, existingTiles)) {
          return { x, y, shape }
        }
      }
    }
  }
  return null
}

export function buildAutoSkillGridLayout(
  skills: readonly AutoLayoutSkill[]
): PlacedTile[] {
  const tiles: PlacedTile[] = []

  for (const skill of skills) {
    if (!skill.slug) continue

    const tier = getSizeTier(skill.skill_md_size ?? 0)
    const shape = getDefaultShape(tier)
    const placement = autoPlace(tier, shape, tiles)

    if (placement) {
      tiles.push({
        slug: skill.slug,
        ...placement,
      })
    }
  }

  return tiles
}

export function getShapeDimensions(shape: ShapeName): {
  cols: number
  rows: number
} {
  const offsets = SHAPE_OFFSETS[shape]
  const maxX = Math.max(...offsets.map(([dx]) => dx))
  const maxY = Math.max(...offsets.map(([, dy]) => dy))
  return { cols: maxX + 1, rows: maxY + 1 }
}

export function isGridFull(existingTiles: readonly PlacedTile[]): boolean {
  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      if (canPlace(x, y, "1x1", existingTiles)) {
        return false
      }
    }
  }
  return true
}
